import { Browser } from '@capacitor/browser'
import { CapacitorCookies } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import type { Session } from '@supabase/supabase-js'
import { getDevice } from 'framework7'
import { f7 } from 'framework7-vue'

import { authFailureContext, recordAuthFailure } from '@/assets/composables/authDiagnostics'
import type { AuthStage } from '@/assets/composables/authDiagnostics'
import { refreshBanStatus } from '@/assets/composables/ban'
import { showDiagnosticError } from '@/assets/composables/diagnostics'
import {
  activateUser,
  getDefaultSettings,
  getSupabaseSession,
  refreshSupabaseSession,
  signInWithOAuth,
  signInWithPassword,
  supabaseSignOut,
} from '@/assets/composables/endpoints'
import {
  failureDetails,
  isAmbiguousAuthCallback,
  isExpectedFailure,
} from '@/assets/composables/errorPolicy'
import { mapSupabaseUser } from '@/assets/composables/mappers'
import type { User } from '@/assets/composables/types'
import { clearStore, notify, oauthScopes, unsyncDeviceToken } from '@/assets/composables/utils'
import store from '@/js/store'

const device = getDevice()
const isCordova = device.cordova

// ─── Auth Paths ─────────────────────────────────────────────────────────────

export const AUTH_CALLBACK_PATH = '/auth/supabase/callback'
export const RESET_PASSWORD_PATH = '/auth/reset-password'

const NATIVE_DEEP_LINK_BASE = 'pelikanec://localhost'

export const getAuthRedirectUrl = (path: string): string => {
  if (isCordova) {
    return `${NATIVE_DEEP_LINK_BASE}${path}`
  }
  return `${location.origin}${path}`
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getSession = async ({ throwOnError = false } = {}) => {
  const {
    data: { session },
    error,
  } = await getSupabaseSession()
  // Existing token/permission checks treat an invalid session as signed out.
  // Initialization opts in to throwing so the failure can be diagnosed.
  if (error && throwOnError) throw error
  return session
}

const cleanupLocalState = async (): Promise<void> => {
  await Promise.all([clearStore(), Preferences.clear(), CapacitorCookies.clearAllCookies()])
}

const withPreloader = async <T>(fn: () => Promise<T>): Promise<T> => {
  f7.preloader.show()
  try {
    return await fn()
  } finally {
    f7.preloader.hide()
  }
}

// ─── Session & Token ─────────────────────────────────────────────────────────

export const checkSupabaseSession = async (): Promise<User | null> => {
  const session = await getSession()
  return session?.user ? mapSupabaseUser(session.user) : null
}

export const getGoogleProviderToken = async (): Promise<string | null> => {
  const session = await getSession()
  return session?.provider_token ?? null
}

export const getAuthorizationToken = async (): Promise<string> => {
  const session = await getSession()
  return session?.access_token || ''
}

// ─── Permissions ─────────────────────────────────────────────────────────────

const handleNoAccess = async (logoutWithNotice: boolean): Promise<false> => {
  if (!logoutWithNotice) {
    await logoutWithoutPrompt()
    return false
  }

  f7.dialog.alert(
    `
      S týmto účtom nemáte prístup k aplikácii. Prosím, kontaktujte Slack channel
      <a class="link external" href="https://pelichat.slack.com/archives/C07QL4CMB" target="_blank">#technicka-podpora</a>.
    `,
    'Nemáte prístup k aplikácii',
    () => logoutWithoutPrompt(),
  )
  return false
}

export const checkUserPermissions = async (logoutWithNotice: boolean = false): Promise<boolean> => {
  const session = await getSession()

  if (!session?.user) {
    return await handleNoAccess(logoutWithNotice)
  }

  const roles: string[] = session.user.app_metadata?.roles || []
  if (!roles.includes('pelikanec-user')) {
    return await handleNoAccess(logoutWithNotice)
  }

  return true
}

// ─── Logout ──────────────────────────────────────────────────────────────────

export const logoutWithoutPrompt = async (notice: string | null = null): Promise<void> => {
  await unsyncDeviceToken()
  await Promise.all([supabaseSignOut().catch(() => {}), cleanupLocalState()])

  if (notice) {
    notify(notice)
  }
}

export const useLogout = () => {
  return () => {
    f7.dialog.confirm('Naozaj sa chcete odhlásiť?', async () => {
      await logoutWithoutPrompt()
      notify('Boli ste úspešne odhlásený')
    })
  }
}

// ─── Authentication ──────────────────────────────────────────────────────────

export const authenticate = async (hostname?: string, isOAuth: boolean = true, loginData?: any) => {
  await withPreloader(async () => {
    try {
      if (isOAuth) {
        await authenticateOAuth(hostname)
      } else {
        await authenticateEmailPassword(loginData)
      }
    } catch (error) {
      await showAuthenticationFailure(isOAuth ? 'oauth' : 'password', error)
    }
  })
}

const authenticateOAuth = async (hostname?: string) => {
  if (isCordova) {
    const { data, error } = await signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: 'pelikanec://localhost/auth/supabase/callback',
        scopes: oauthScopes.join(' '),
        skipBrowserRedirect: true,
      },
    })
    if (error) throw error
    if (data.url) {
      await Browser.open({ url: data.url, presentationStyle: 'popover' })
    }
  } else {
    const { error } = await signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: hostname || window.location.origin,
        scopes: oauthScopes.join(' '),
      },
    })
    if (error) throw error
  }
}

const authenticateEmailPassword = async (loginData: any) => {
  const { error } = await signInWithPassword(loginData.email, loginData.password)

  if (error) {
    throw error
  }

  await setActiveUserIfAny()
}

// ─── User State ──────────────────────────────────────────────────────────────

export const showAuthenticationFailure = async (
  stage: AuthStage,
  error: unknown,
  session: Session | null = null,
  elapsedMs = 0,
) => {
  if (stage === 'callback' && !session && isAmbiguousAuthCallback(error)) {
    try {
      session = await getSession()
    } catch {
      // Without a confirmed existing session, keep this failure diagnostic.
    }
  }
  const failure = failureDetails(error)
  const expected = isExpectedFailure(error, authFailureContext(session))
  // Failed sign-in attempts and replayed callbacks must not discard an existing
  // valid session. Only failed application initialization uses logout cleanup.
  const logoutOnDismiss = !['oauth', 'password', 'callback'].includes(stage)
  const accessDenied =
    failure.status === 403 &&
    (failure.code === 'ROLE_CONFLICT' || failure.code === 'PERMISSION_DENIED')
  const expiredSession =
    ['session_not_found', 'refresh_token_not_found', 'refresh_token_already_used'].includes(
      failure.code ?? '',
    ) || failure.name === 'AuthSessionMissingError'
  const expiredLink =
    ['flow_state_not_found', 'flow_state_expired', 'bad_code_verifier', 'otp_expired'].includes(
      failure.code ?? '',
    ) || failure.name === 'AuthPKCECodeVerifierMissingError'
  const message = !expected
    ? 'Nepodarilo sa dokončiť prihlásenie. Skúste to znova, prípadne cez inú sieť. Ak problém pretrváva, pošlite diagnostiku technickej podpore.'
    : accessDenied
      ? 'Server zamietol prístup k aplikácii. Kontaktujte technickú podporu.'
      : failure.code === 'invalid_credentials' || failure.name === 'AuthInvalidCredentialsError'
        ? 'Nesprávny e-mail alebo heslo.'
        : failure.code === 'email_not_confirmed'
          ? 'Pred prihlásením potvrďte svoju e-mailovú adresu.'
          : expiredSession
            ? 'Platnosť prihlásenia vypršala. Prihláste sa prosím znova.'
            : expiredLink
              ? 'Prihlasovací odkaz už nie je platný. Skúste sa prihlásiť znova.'
              : failure.status === 429 ||
                  failure.code?.startsWith('over_') ||
                  failure.code === 'RATE_LIMIT_EXCEEDED'
                ? 'Príliš veľa pokusov o prihlásenie. Skúste to prosím neskôr.'
                : failure.code === 'user_banned'
                  ? 'Tento účet je zablokovaný. Kontaktujte technickú podporu.'
                  : 'Prihlásenie nebolo možné dokončiť. Skontrolujte prihlasovacie údaje alebo kontaktujte podporu.'
  const title = accessDenied ? 'Nemáte prístup k aplikácii' : 'Prihlásenie sa nepodarilo'
  const onDismiss = () => {
    if (logoutOnDismiss) void logoutWithoutPrompt()
  }
  if (expected) {
    f7.dialog.alert(message, title, onDismiss)
    return
  }
  const report = await recordAuthFailure(stage, error, session, elapsedMs)
  if (report) showDiagnosticError(report, { title, message, onDismiss })
}

export const setActiveUserIfAny = async (): Promise<boolean> => {
  let session: Session | null = null
  let stage: AuthStage = 'session'
  const startedAt = Date.now()

  try {
    session = await getSession({ throwOnError: true })
    if (session?.user) {
      stage = 'activation'
      const activateData: { companyId?: string; companyName?: string } = await activateUser()
      stage = 'refresh'
      const refreshed = await refreshSupabaseSession()
      if (refreshed.error) throw refreshed.error
      if (!refreshed.data.session) throw new Error('Missing refreshed session')
      session = refreshed.data.session

      const supabaseUser = {
        ...mapSupabaseUser(session.user),
        companyId: activateData.companyId,
        companyName: activateData.companyName,
      }
      stage = 'settings'
      const defaultSettings = await getDefaultSettings()
      stage = 'store'
      await Promise.all([
        store.dispatch('setDefaultSettings', defaultSettings),
        store.dispatch('setUser', supabaseUser),
      ])
      await refreshBanStatus()
      return true
    }

    await cleanupLocalState()
    await store.dispatch('setUser', null)
    return false
  } catch (error) {
    await showAuthenticationFailure(stage, error, session, Date.now() - startedAt)
    return false
  }
}

export const recheckForUpdate = async (checkForUpdate: () => Promise<boolean>) => {
  try {
    const supabaseUser = await checkSupabaseSession()
    if (!supabaseUser) return

    const defaultSettings = await getDefaultSettings()
    await store.dispatch('setDefaultSettings', defaultSettings)
    await checkForUpdate()
  } catch {}
}

export const checkTokenValidity = async (): Promise<boolean> => {
  const supabaseUser = await checkSupabaseSession()
  if (!supabaseUser) {
    await logoutWithoutPrompt('Platnosť tokenu vypršala. Prosím, prihláste sa znova.')
    return false
  }

  return true
}
